import classNames from 'classnames/bind';
import PropTypes from 'prop-types';
import * as React from 'react';
import { shareArrow } from '../../shared/vector-images';
import ResponsiveModal from '../../shared/_deprecated/components/ResponsiveModal/ResponsiveModal';
import DeprecatedButton from '../../shared/_deprecated/elements/Button/Button';
import CopyLink from './CopyLink';
import InviteFriend from './InviteFriend';
import ShareNow from './ShareNow';
import styles from './SharingDialog.scss';
import { appendInvitationParam } from '../../../services/member/sharing';
import { getURLFromPath } from '../../helpers/share-helpers';

const cx = classNames.bind(styles);
const INVITE_FRIEND = 'invite-friend';
const SHARE_NOW = 'share-now';
const MORE_OPTIONS = 'more-options';

const SharingDialog = ({
  isOpen,
  closeAction,
  className,
  path,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
  socialText,
  title,
  description,
  type,
  id,
  currentMemberImage,
  currentMemberUsername,
  currentMemberTitle,
}) => {
  // path = the path section of the URL
  // text = the social media sharing text - rename to socialText
  // title = the page or marker or hazard title
  // description = the page or marker or hazard description
  // type = the marker type or weather or trip or ... (used along with ID to look up additional info)
  // id = the internal ID of the item being shared.

  const [currentMode, setCurrentMode] = React.useState(SHARE_NOW);
  const [optionsVisible, setOptionsVisible] = React.useState(true);
  const [submissionSuccess, setSubmissionSuccess] = React.useState(false);

  const viewClasses = cx('sharing-dialog', {
    [className]: className,
  });

  React.useEffect(() => {
    let isMounted = true;

    // this is here to make sure we always open to share now
    if (isOpen && isMounted) {
      setCurrentMode(SHARE_NOW);
      setOptionsVisible(true);
      setSubmissionSuccess(false);
    }

    const updatedPath = appendInvitationParam(getURLFromPath(path));

    return () => {
      isMounted = false;
    };
  }, [isOpen]);

  const successAction = React.useCallback(() => {
    // hide the links
    setOptionsVisible(false);

    // change modal to message style
    setSubmissionSuccess(true);

    // close the success message
    setTimeout(() => {
      closeAction();
    }, 3000);
  }, [closeAction]);

  if (isOpen) {
    return (
      <ResponsiveModal
        className={viewClasses}
        onBackdropClick={closeAction}
        useMessageStyleModal={submissionSuccess}
      >
        {currentMode === INVITE_FRIEND ? (
          <InviteFriend
            path={path}
            title={title}
            description={description}
            type={type}
            id={id}
            currentMemberImage={currentMemberImage}
            currentMemberUsername={currentMemberUsername}
            currentMemberTitle={currentMemberTitle}
            onSuccess={successAction}
            onDismiss={closeAction}
            submissionSuccess={submissionSuccess}
          />
        ) : null}

        {currentMode === SHARE_NOW ? (
          <ShareNow
            path={path}
            title={title}
            description={description}
            type={type}
            id={id}
            currentMemberImage={currentMemberImage}
            currentMemberUsername={currentMemberUsername}
            currentMemberTitle={currentMemberTitle}
            onSuccess={successAction}
            onDismiss={closeAction}
            submissionSuccess={submissionSuccess}
          />
        ) : null}

        {currentMode === MORE_OPTIONS ? (
          <CopyLink
            path={path}
            onSuccess={successAction}
            submissionSuccess={submissionSuccess}
          />
        ) : null}

        {optionsVisible ? (
          <div className={cx('sharing-dialog__footer-buttons')}>
            {currentMode !== SHARE_NOW ? (
              <DeprecatedButton
                className={cx('sharing-dialog__footer-button')}
                icon={shareArrow()}
                type="basic"
                size="medium"
                fullWidth
                action={() => setCurrentMode(SHARE_NOW)}
              >
                Share now
              </DeprecatedButton>
            ) : null}
          </div>
        ) : null}
      </ResponsiveModal>
    );
  }

  return null;
};

SharingDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  closeAction: PropTypes.func.isRequired,
  path: PropTypes.string,
  title: PropTypes.string,
  description: PropTypes.string,
  type: PropTypes.string,
  id: PropTypes.string,
  socialText: PropTypes.string,
  currentMemberImage: PropTypes.string,
  currentMemberTitle: PropTypes.string,
  currentMemberUsername: PropTypes.string,
  className: PropTypes.string,
};

SharingDialog.defaultProps = {
  className: undefined,
  id: undefined,
  socialText: undefined,
  type: undefined,
  description: undefined,
  title: undefined,
  path: undefined,
  currentMemberImage: undefined,
  currentMemberTitle: undefined,
  currentMemberUsername: undefined,
};

export default SharingDialog;
